import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useCountryScope, usePermissions } from '@/features/auth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { PageContainer } from '@/shared/components/page-container';
import { ExportActions } from '@/shared/components/export-actions';
import { PageHeader } from '@/shared/components/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatCurrency } from '@/shared/lib/format';

import { exportProviders } from '../api/providers.api';
import { useProviders } from '../api/providers.queries';
import { ProviderCreateDialog } from '../components/provider-dialogs';
import type { Provider } from '../types/provider.types';

const ANY = 'all';

const COLUMNS: ColumnDef<Provider, unknown>[] = [
  {
    id: 'name',
    header: 'Provider',
    enableSorting: false,
    enableHiding: false,
    meta: { label: 'Provider' },
    cell: ({ row }) => (
      <Link
        to={`/providers/${row.original.id}`}
        className="text-primary hover:text-primary/80 font-medium hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    enableSorting: false,
    meta: { label: 'Status' },
    cell: ({ row }) => (
      <StatusBadge variant={row.original.isActive ? 'success' : 'neutral'}>
        {row.original.isActive ? 'Active' : 'Inactive'}
      </StatusBadge>
    ),
  },
  {
    id: 'balance',
    header: () => <div className="text-end">Balance</div>,
    enableSorting: false,
    meta: { label: 'Balance' },
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-1.5 tabular-nums">
        {row.original.isLowBalance && <StatusBadge variant="danger">Low</StatusBadge>}
        {formatCurrency(row.original.balance, row.original.currencyCode ?? 'USD')}
      </div>
    ),
  },
  {
    id: 'mappings',
    header: () => <div className="text-end">Mappings in use</div>,
    enableSorting: false,
    meta: { label: 'Mappings' },
    cell: ({ row }) => (
      <div className="text-end tabular-nums">
        {row.original.activeMappingCount}
        <span className="text-muted-foreground"> / {row.original.mappingCount}</span>
      </div>
    ),
  },
  {
    id: 'sole',
    header: () => <div className="text-end">Sole provider for</div>,
    enableSorting: false,
    meta: { label: 'Sole provider for' },
    cell: ({ row }) => (
      <div className="text-end tabular-nums">{row.original.soleProviderCount} items</div>
    ),
  },
];

/**
 * Providers — who supplies our voucher codes, how much prepaid balance we
 * hold with each, and how much of the catalogue depends on each alone.
 * Adding one needs providers.manage and all-country scope.
 */
export function ProvidersPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const canManage = can('providers.manage') && allCountries;
  const [creating, setCreating] = useState(false);
  const tableState = useTableSearchParams();
  const debouncedSearch = useDebouncedValue(tableState.search, 300);
  const statusFilter = tableState.getFilter('status');
  const balanceFilter = tableState.getFilter('balance');

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: debouncedSearch || undefined,
      isActive: statusFilter === null ? undefined : statusFilter === 'active',
      lowBalance: balanceFilter === 'low' ? true : undefined,
    }),
    [tableState.page, tableState.pageSize, debouncedSearch, statusFilter, balanceFilter],
  );

  const providers = useProviders(params);
  const table = useDataTable({
    data: providers.data,
    columns: COLUMNS,
    tableState,
    getRowId: (provider) => String(provider.id),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Providers"
        description="The integrations that supply voucher codes: status, prepaid balance and the items that depend on each."
        actions={
          <>
            <ExportActions
              excel={() => {
                const { page: _page, pageSize: _pageSize, ...filters } = params;
                return exportProviders(filters);
              }}
            />
            {canManage && (
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="size-4" aria-hidden />
                New provider
              </Button>
            )}
          </>
        }
      />
      {creating && (
        <ProviderCreateDialog
          onClose={() => setCreating(false)}
          onCreated={(providerId) => void navigate(`/providers/${providerId}`)}
        />
      )}
      <div className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
        >
          <SearchInput
            value={tableState.search}
            onChange={tableState.setSearch}
            placeholder="Search providers…"
            className="w-full sm:w-56"
          />
          <Select
            value={statusFilter ?? ANY}
            onValueChange={(value) => tableState.setFilter('status', value === ANY ? null : value)}
          >
            <SelectTrigger size="sm" className="h-8 w-32 text-xs" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Any status
              </SelectItem>
              <SelectItem value="active" className="text-xs">
                Active
              </SelectItem>
              <SelectItem value="inactive" className="text-xs">
                Inactive
              </SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={balanceFilter ?? ANY}
            onValueChange={(value) => tableState.setFilter('balance', value === ANY ? null : value)}
          >
            <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Filter by balance">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Any balance
              </SelectItem>
              <SelectItem value="low" className="text-xs">
                Low balance
              </SelectItem>
            </SelectContent>
          </Select>
        </DataTableToolbar>

        <DataTable
          table={table}
          isLoading={providers.isLoading}
          isFetching={providers.isFetching}
          error={providers.error}
          onRetry={() => void providers.refetch()}
          emptyState={
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={Truck}
                title="No providers match your filters"
                description="Clear the filters to see every provider."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Truck}
                title="No providers"
                description="Providers supply the voucher codes; each needs an integration in the platform code."
              />
            )
          }
        />
        <DataTablePagination table={table} />
      </div>
    </PageContainer>
  );
}
